/**
 * SharedPlan — public read-only view of a project, accessed via a share token.
 *
 * URL: /share/plans/:token
 * No auth required. Renders the project's modules using ReadOnlyModule
 * (the same component used by the catalog detail page). Includes a top
 * banner showing the project title, view count, expiry, and an unobtrusive
 * call-to-action that promotes Atelier Business.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowUpRight,
  Eye,
  Calendar,
  Sparkles,
  FileText,
  Lightbulb,
  Search,
  Cog,
  Users,
  Calendar as CalendarIcon,
  Megaphone,
  Wallet,
  ShieldAlert,
  Grid3x3,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PrimaryButton, Overline } from "@/components/ui-kit";
import ReadOnlyModule from "@/components/ReadOnlyModule";

const MODULES = [
  { key: "executive_summary", icon: FileText },
  { key: "canvas", icon: Grid3x3 },
  { key: "concept", icon: Lightbulb },
  { key: "market", icon: Search },
  { key: "operations", icon: Cog },
  { key: "hr", icon: Users },
  { key: "calendar", icon: CalendarIcon },
  { key: "marketing", icon: Megaphone },
  { key: "budget", icon: Wallet },
  { key: "risks", icon: ShieldAlert },
];

export default function SharedPlan() {
  const { token } = useParams();
  const { t, lang } = useLang();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState("executive_summary");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/share/plans/${token}`);
      setPlan(res.data);
    } catch (e) {
      setError(
        formatApiErrorDetail(e.response?.data?.detail) ||
          (lang === "es" ? "Enlace inválido, revocado o expirado." : "Invalid, revoked or expired link.")
      );
    } finally {
      setLoading(false);
    }
  }, [token, lang]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="text-[#5C5B5A] text-sm tracking-[0.2em] uppercase">{t("common.loading")}</div>
      </div>
    );
  }
  if (error || !plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6">
        <div className="text-center max-w-md" data-testid="share-error-state">
          <h2
            className="text-3xl text-[#1A1A1A]"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
          >
            {lang === "es" ? "Enlace no disponible" : "Link unavailable"}
          </h2>
          <p className="mt-3 text-sm text-[#5C5B5A]">{error}</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-1 text-[#2563EB] underline">
            {lang === "es" ? "Volver al inicio" : "Back home"}
          </Link>
        </div>
      </div>
    );
  }

  const modules = plan.modules || {};
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* HEADER */}
      <header className="border-b border-[#E8E6E1] bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <span
              className="text-xl tracking-tight text-[#1A1A1A]"
              style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
            >
              Atelier <span className="italic font-normal text-[#B89154]">Business</span>
            </span>
            <div className="h-6 w-px bg-[#E8E6E1]" />
            <Overline>{lang === "es" ? "Plan compartido" : "Shared plan"}</Overline>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <LanguageSwitcher />
            <Link to="/register">
              <PrimaryButton testId="share-cta-btn">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {lang === "es" ? "Crear el mío" : "Make mine"}
              </PrimaryButton>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative bg-[#2563EB] text-white overflow-hidden">
        {plan.image_url && (
          <img src={plan.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        )}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
          <div className="w-12 h-1 bg-[#B89154] mb-6" />
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
            data-testid="share-plan-title"
          >
            {plan.name}
            {plan.version_label && (
              <span className="ml-3 align-middle text-base font-normal text-[#F5EFE6]/60" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                · {plan.version_label}
              </span>
            )}
          </h1>
          {plan.description && (
            <p className="mt-4 text-base lg:text-lg text-[#F5EFE6]/85 max-w-3xl">{plan.description}</p>
          )}
          <div className="mt-6 flex flex-wrap gap-4 items-center text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">
            <span className="inline-flex items-center gap-1.5">
              <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
              {plan.share_meta?.views ?? 0} {lang === "es" ? "visitas" : "views"}
            </span>
            {plan.share_meta?.expires_at && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
                {lang === "es" ? "Caduca" : "Expires"}{" "}
                {new Date(plan.share_meta.expires_at).toLocaleDateString(lang === "es" ? "es-ES" : "en-US")}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="lg:sticky lg:top-24 self-start">
          <Overline>{lang === "es" ? "Módulos" : "Modules"}</Overline>
          <nav className="mt-4 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible" data-testid="share-module-nav">
            {MODULES.map((m, idx) => {
              const Icon = m.icon;
              const isActive = m.key === active;
              const has = !!modules[m.key];
              return (
                <button
                  key={m.key}
                  onClick={() => setActive(m.key)}
                  disabled={!has}
                  className={`flex items-center gap-3 px-4 py-3 text-sm border-l-2 transition-all whitespace-nowrap ${
                    isActive
                      ? "bg-white border-[#2563EB] text-[#2563EB] font-medium"
                      : has
                      ? "border-transparent text-[#5C5B5A] hover:text-[#1A1A1A] hover:bg-white/60"
                      : "border-transparent text-[#C8C7C2] cursor-not-allowed"
                  }`}
                  data-testid={`share-module-tab-${m.key}`}
                >
                  <span
                    className="font-mono text-xs text-[#8A8886]"
                    style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  <span className="flex-1 text-left">{t(`module.${m.key}`)}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <section className="space-y-6 min-w-0" data-testid="share-module-content">
          {modules[active] ? (
            <ReadOnlyModule moduleKey={active} data={modules[active]} project={{ name: plan.name, description: plan.description }} />
          ) : (
            <div className="bg-white border border-[#E8E6E1] rounded-sm p-12 text-center">
              <p className="text-sm text-[#5C5B5A]">
                {lang === "es" ? "Sin contenido para este módulo." : "No content for this module."}
              </p>
            </div>
          )}

          {/* Bottom CTA */}
          <div className="mt-12 bg-[#EFF4FF] border border-[#2563EB]/20 rounded-sm p-8 text-center" data-testid="share-bottom-cta">
            <Overline>{lang === "es" ? "Hecho con Atelier Business" : "Made with Atelier Business"}</Overline>
            <h3 className="mt-3 text-2xl text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
              {lang === "es" ? "Diseña tu propio plan de negocio" : "Design your own business plan"}
            </h3>
            <p className="mt-2 text-sm text-[#5C5B5A] max-w-xl mx-auto">
              {lang === "es"
                ? "Crea, edita, comparte y presenta tu plan de negocio con la misma calidad editorial. Pruébalo gratis."
                : "Create, edit, share and pitch your plan with the same editorial quality. Try it free."}
            </p>
            <div className="mt-5 inline-flex gap-3">
              <Link to="/register">
                <PrimaryButton testId="share-bottom-cta-btn">
                  <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                  {lang === "es" ? "Empezar gratis" : "Start free"}
                  <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                </PrimaryButton>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
